import { mapInfo, type MapId } from "@69ly/maps";
import { GRENADES, IMPORTANCES, importanceRank, type Grenade, type Importance, type LearnStatus, type Side, type TeamDetail } from "@69ly/shared";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCallouts } from "../lib/callouts.ts";
import { useTracked } from "../lib/live.ts";
import { useStuffs } from "../lib/stuff-queries.ts";
import { Avatar } from "./Avatar.tsx";
import { Icon } from "./Icon.tsx";
import { ImportanceMark } from "./Importance.tsx";
import { GrenadeIcon } from "./MarkerIcon.tsx";
import { ErrorState, Loading } from "./Status.tsx";
import { StuffLink } from "./StuffDrawer.tsx";

/** Stuffs a page: enough to see a site's utility at once, short enough to scan. */
export const WHO_PAGE = 20;

type SideFilter = "both" | Side;
type SiteFilter = "all" | "a" | "b" | "mid";

const toggle = <T,>(list: T[], x: T) => (list.includes(x) ? list.filter((y) => y !== x) : [...list, x]);

/**
 * Who knows what on the Team page's map: every stuff of the team's library
 * against every member, with their learning status. Filter by grenade,
 * importance, side and site; the most important first, 20 a page. The counts
 * under each player cover everything the filters keep, not just the page.
 */
export function WhoKnowsWhat({ team, map }: { team: TeamDetail; map: MapId }) {
  const { t } = useTranslation();
  const [grenades, setGrenades] = useState<Grenade[]>([]);
  const [importance, setImportance] = useState<Importance[]>([]);
  const [side, setSide] = useState<SideFilter>("both");
  const [site, setSite] = useState<SiteFilter>("all");
  const [page, setPage] = useState(0);
  const stuffs = useStuffs(team.id, map);
  const tracked = useTracked(team.id, map);
  const callouts = useCallouts();

  // Another map or another filter: back to the first page.
  const filterKey = `${map}|${grenades.join()}|${importance.join()}|${side}|${site}`;
  useEffect(() => setPage(0), [filterKey]);

  const status = useMemo(() => {
    const out = new Map<string, LearnStatus>();
    for (const r of tracked.data ?? []) out.set(`${r.stuffId}:${r.userId}`, r.status);
    return out;
  }, [tracked.data]);

  const rows = useMemo(
    () =>
      (stuffs.data ?? [])
        .filter(
          (s) =>
            (grenades.length === 0 || grenades.includes(s.grenade)) &&
            (importance.length === 0 || importance.includes(s.importance)) &&
            (side === "both" || s.side === side) &&
            (site === "all" || s.area === site),
        )
        .sort((a, b) => importanceRank(a.importance) - importanceRank(b.importance) || a.title.localeCompare(b.title, undefined, { numeric: true })),
    [stuffs.data, grenades, importance, side, site],
  );
  const pages = Math.max(1, Math.ceil(rows.length / WHO_PAGE));
  const at = Math.min(page, pages - 1);
  const shown = rows.slice(at * WHO_PAGE, at * WHO_PAGE + WHO_PAGE);
  const learnedBy = (userId: string) => rows.filter((s) => status.get(`${s.id}:${userId}`) === "learned").length;
  const filtered = grenades.length > 0 || importance.length > 0 || side !== "both" || site !== "all";
  const total = stuffs.data?.length ?? 0;

  return (
    <section className="card who-knows" aria-labelledby="who-title">
      <div className="who-head">
        <h2 id="who-title" className="eyebrow">
          {t("who.title")}
        </h2>
        <span className="muted small">{filtered ? t("who.shownOf", { count: rows.length, total }) : t("who.stuff", { count: rows.length })}</span>
      </div>
      <div className="who-filters">
        <div className="quick-grenades" role="group" aria-label={t("mapView.types")}>
          {GRENADES.map((g) => (
            <button key={g} type="button" className="quick-chip" aria-pressed={grenades.includes(g)} aria-label={t(`grenade.${g}`)} title={t(`grenade.${g}`)} onClick={() => setGrenades(toggle(grenades, g))}>
              <GrenadeIcon grenade={g} side={side === "both" ? "t" : side} neutral={side === "both"} size={22} />
            </button>
          ))}
        </div>
        <div className="segmented segmented-small who-importance" role="group" aria-label={t("importance.label")}>
          {IMPORTANCES.map((i) => (
            <button key={i} type="button" className="seg" aria-pressed={importance.includes(i)} title={t(`importance.help.${i}`)} onClick={() => setImportance(toggle(importance, i))}>
              <ImportanceMark importance={i} />
              {t(`importance.${i}`)}
            </button>
          ))}
        </div>
        <div className="segmented segmented-small" role="group" aria-label={t("who.side")}>
          {(["both", "t", "ct"] as const).map((s) => (
            <button key={s} type="button" className={`seg${s === "both" ? "" : ` side-seg-${s}`}`} aria-pressed={side === s} onClick={() => setSide(s)}>
              {s === "both" ? t("who.bothSides") : t(`sideShort.${s}`)}
            </button>
          ))}
        </div>
        <div className="segmented segmented-small" role="group" aria-label={t("who.site")}>
          {(["all", "a", "b", "mid"] as const).map((a) => (
            <button key={a} type="button" className="seg" aria-pressed={site === a} onClick={() => setSite(a)}>
              {t(`areaShort.${a}`)}
            </button>
          ))}
        </div>
        {filtered && (
          <button
            type="button"
            className="btn btn-quiet btn-small"
            onClick={() => {
              setGrenades([]);
              setImportance([]);
              setSide("both");
              setSite("all");
            }}
          >
            {t("who.clear")}
          </button>
        )}
      </div>
      {stuffs.isPending || tracked.isPending ? (
        <Loading />
      ) : stuffs.error ? (
        <ErrorState message={t("common.error")} onRetry={() => void stuffs.refetch()} />
      ) : rows.length === 0 ? (
        <p className="muted">{filtered ? t("who.noMatch") : t("who.empty", { map: mapInfo(map).name })}</p>
      ) : (
        <>
          <div className="who-scroll">
            <table className="who-table">
              <thead>
                <tr>
                  <th scope="col">{t("who.stuffColumn")}</th>
                  {team.members.map((m) => (
                    <th key={m.userId} scope="col" className="who-member">
                      <Avatar name={m.name} url={m.avatarUrl} size={26} />
                      <span>{m.name}</span>
                      <span className="muted small">{t("who.learnedCount", { count: learnedBy(m.userId), total: rows.length })}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((s) => {
                  const to = callouts.places(map, s).to;
                  return (
                    <tr key={s.id}>
                      <th scope="row">
                        <StuffLink id={s.id} className="who-stuff">
                          <GrenadeIcon grenade={s.grenade} side={s.side} size={22} />
                          <span>
                            {s.title}
                            {to && <span className="muted small"> · {to}</span>}
                          </span>
                          <span className="who-importance-mark" title={t(`importance.${s.importance}`)}>
                            <ImportanceMark importance={s.importance} />
                            <span className="visually-hidden">{t(`importance.${s.importance}`)}</span>
                          </span>
                        </StuffLink>
                      </th>
                      {team.members.map((m) => {
                        const st = status.get(`${s.id}:${m.userId}`);
                        return (
                          <td key={m.userId} className={`who-cell${st ? ` status-${st}` : ""}`} title={`${m.name}: ${st ? t(`status.${st}`) : t("status.none")}`}>
                            <span className="who-dot" aria-hidden="true" />
                            <span className="visually-hidden">{st ? t(`status.${st}`) : t("status.none")}</span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <nav className="who-pages" aria-label={t("who.pages")}>
              <button type="button" className="btn btn-quiet btn-small" disabled={at === 0} onClick={() => setPage(at - 1)}>
                <Icon name="chevronLeft" size={14} />
                {t("who.previous")}
              </button>
              <span className="small muted">
                {t("who.range", { from: at * WHO_PAGE + 1, to: at * WHO_PAGE + shown.length, total: rows.length })} · {t("who.page", { n: at + 1, count: pages })}
              </span>
              <button type="button" className="btn btn-quiet btn-small" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>
                {t("who.next")}
                <Icon name="chevronRight" size={14} />
              </button>
            </nav>
          )}
        </>
      )}
      <p className="muted small who-legend">
        {(["learned", "practising", "to_learn"] as const).map((s) => (
          <span key={s} className={`who-key status-${s}`}>
            <span className="who-dot" aria-hidden="true" />
            {t(`status.${s}`)}
          </span>
        ))}
        <span className="who-key">
          <span className="who-dot" aria-hidden="true" />
          {t("status.none")}
        </span>
      </p>
    </section>
  );
}
